import type { Game } from '../../core/game';
import type { TelemetryChannel } from '../../telemetry/types';
import { configureGateEvents, installGateEventsFlush, trackGateEvent } from '../../telemetry/events';
import { claimGate, hasGateApi, setEntitled } from '../gameApi';
import {
  clearEntitlement,
  onEntitlementChange,
  readEntitlement,
  writeEntitlement,
  type Entitlement,
  type EntitlementSource,
} from './store';
import { getPlayBilling, type PlayBilling } from './play';
import { configureAck, installAckFlush, scheduleAck } from './ack';
import { readPref, removePref, writePref } from '../prefs';

/**
 * Module d'entitlement (A112) : relie l'état « jeu complet » de l'appareil
 * (`store.ts`, `localStorage`) au booléen du cœur (`game.setEntitled`).
 *
 *  1. au lancement, **synchrone** : `game.setEntitled(readEntitlement() !== null)`
 *     — le jeu connaît l'état avant la première Accalmie ;
 *  2. puis, asynchrone et silencieux, **dans la TWA seulement** : Google Play est
 *     interrogé (`listPurchases`). Un achat retrouvé ouvre l'appareil (nouvel
 *     appareil, réinstallation : événement `restore_ok`) et son jeton est
 *     transmis au Worker pour être **acquitté** (`ack.ts`, A118) ; un achat Play
 *     qui a **disparu** (remboursement) ne referme l'appareil qu'après
 *     **trois lancements consécutifs** sans achat connu (A118, événement
 *     `revoked`) et **seulement sur une réponse claire** — une erreur réseau ne
 *     change rien ;
 *  3. tout changement du magasin local rejoint le cœur.
 *
 * L'état n'est jamais dans la sauvegarde. Sans l'API du cœur (avant la fusion
 * du lot A112), le module ne fait rien.
 */

export interface InstallOptions {
  /** Version du jeu, pour les événements du mur. */
  version: string;
  channel: TelemetryChannel;
  /** Pour les tests : fournisseur de Google Play. */
  billing?: () => Promise<PlayBilling | null>;
  /** Pour les tests : pas d'envoi de fond des événements. */
  backgroundFlush?: boolean;
}

export interface EntitlementApi {
  /** Ouvre l'appareil : écrit, pose, `claimGate()` si dû ; renvoie le résultat de `claimGate`. */
  unlockWith(source: EntitlementSource, token?: string): boolean;
  /** Referme l'appareil (debug) : `setEntitled(false)`. */
  revokeEntitlement(): void;
  /** L'ouverture enregistrée sur cet appareil, `null` si fermé. */
  current(): Entitlement | null;
  /** Relit Google Play (TWA) ; résolu quand c'est fait, jamais rejeté. */
  syncWithPlay(): Promise<void>;
  /** Détache les écouteurs (tests). */
  dispose(): void;
}

/** Lancements consécutifs où Play a répondu « non possédé » (A118). */
export const PLAY_MISSING_KEY = 'diceordie.play.missing';
/** Lancements consécutifs sans achat connu avant de refermer un appareil ouvert par un achat Play. */
export const PLAY_MISSING_BEFORE_REVOKE = 3;

/**
 * Compteur de « non possédé » après une réponse claire de Play : incrémente, et demande la
 * révocation (compteur remis à zéro) quand il atteint `PLAY_MISSING_BEFORE_REVOKE`. Pure.
 */
export function nextMissing(count: number): { count: number; revoke: boolean } {
  const n = (Number.isFinite(count) && count > 0 ? Math.floor(count) : 0) + 1;
  return n >= PLAY_MISSING_BEFORE_REVOKE ? { count: 0, revoke: true } : { count: n, revoke: false };
}

/** Le compteur gardé sur l'appareil (lecture tolérante : illisible → 0). */
export function readMissing(): number {
  const n = Number(readPref(PLAY_MISSING_KEY));
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function writeMissing(count: number): void {
  if (count > 0) writePref(PLAY_MISSING_KEY, String(count));
  else removePref(PLAY_MISSING_KEY);
}

let installed: EntitlementApi | null = null;

/** Le module installé, `null` avant `installEntitlement` (ou sans l'API du cœur). */
export function getEntitlement(): EntitlementApi | null {
  return installed;
}

export function installEntitlement(game: Game, opts: InstallOptions): EntitlementApi {
  const gate = hasGateApi(game);
  const billing = opts.billing ?? getPlayBilling;

  configureGateEvents({
    version: opts.version,
    channel: opts.channel,
    saveId: () => (game.state as unknown as { saveId?: string }).saveId,
    canSend: () => game.state.phase !== 'expedition' && game.state.phase !== 'invasion',
  });

  configureAck({ level: () => game.state.level });

  // 1. Lecture locale synchrone : le cœur sait avant la première Accalmie.
  if (gate) setEntitled(game, readEntitlement() !== null);

  // 3. Le magasin local est la source de vérité de l'appareil.
  const offStore = onEntitlementChange((e) => {
    if (gate) setEntitled(game, e !== null);
  });

  const api: EntitlementApi = {
    unlockWith(source, token) {
      if (source === 'debug') {
        try {
          game.markTainted();
        } catch {
          /* le marquage ne doit pas empêcher l'ouverture */
        }
      }
      writeEntitlement({ source, ...(token ? { token } : {}), at: new Date().toISOString() });
      writeMissing(0); // une ouverture neuve repart d'un compteur de « non possédé » à zéro
      // Achat Play : le Worker l'acquitte (sinon Google le rembourse). En file, jamais bloquant.
      if (source === 'play' && token) scheduleAck(token);
      if (!gate) return false;
      setEntitled(game, true);
      return claimGate(game);
    },

    revokeEntitlement() {
      clearEntitlement();
      writeMissing(0);
      if (gate) setEntitled(game, false);
    },

    current: () => readEntitlement(),

    async syncWithPlay() {
      try {
        const play = await billing();
        if (!play) return;
        const known = await play.ownedKnown();
        if (known === null) return; // Play ne répond pas : rien ne change
        const stored = readEntitlement();
        if (known) {
          writeMissing(0);
          if (!stored) {
            api.unlockWith('play');
            trackGateEvent('restore_ok', { detail: 'play', level: game.state.level });
          }
          // Un achat retrouvé (restauré ou déjà connu) peut ne pas avoir été acquitté : le Worker tranche
          // (`already` si Google le donnait acquitté).
          const tokens = typeof play.ownedTokens === 'function' ? await play.ownedTokens() : null;
          for (const token of tokens ?? []) scheduleAck(token);
        } else if (stored?.source === 'play') {
          // Google Play ne connaît plus l'achat : remboursement, ou simple raté de Play (client de
          // facturation pas encore connecté, compte changé un instant) ? On ne referme qu'au troisième
          // lancement consécutif.
          const next = nextMissing(readMissing());
          if (next.revoke) {
            api.revokeEntitlement();
            trackGateEvent('revoked', { detail: 'play_missing', level: game.state.level });
          } else {
            writeMissing(next.count);
          }
        }
      } catch {
        /* silencieux : le jeu n'attend jamais Google Play (A79) */
      }
    },

    dispose() {
      offStore();
      offAck();
      if (installed === api) installed = null;
    },
  };
  installed = api;

  // 2. Asynchrone et silencieux.
  void api.syncWithPlay();
  if (opts.backgroundFlush !== false) installGateEventsFlush();
  // Acquittement en attente (achat dont le Worker n'a pas encore répondu) : ≈ 2 s après le lancement, puis au retour du réseau.
  const offAck = opts.backgroundFlush !== false ? installAckFlush() : () => undefined;
  return api;
}
